import { Component, OnDestroy, OnInit } from '@angular/core';
import { StepperService } from '../../../stepper/stepper.service';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ShipmentService } from 'src/app/services/shipment.service';
import { MessageService } from 'primeng/api';
import { CommonModule, formatDate } from '@angular/common';
import { AuthService, OBPermisssionsLocal } from 'src/app/services/auth.service';
import { Router } from '@angular/router';
import { ServicesService } from 'src/app/services/services.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AddCustomerAddress, CreateCustomer, CustomerAddress } from 'src/app/domain/Customer';
import { CustomerService } from 'src/app/services/customer.service';
import { ExternalCarrierAddressComponent } from '../../../directives/external-carrier-address/external-carrier-address.component';
import { Subject, takeUntil } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { RippleModule } from 'primeng/ripple';

type GLSBookingType = 'BUSINESS' | 'PARCELSHOP' | 'PRIVATE' | 'ABROAD';

@Component({
  selector: 'app-gls-booking',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, TranslateModule,
    ButtonModule, RadioButtonModule, InputNumberModule, InputTextModule, ProgressSpinnerModule, TooltipModule, RippleModule,
    ExternalCarrierAddressComponent,],
  templateUrl: './glsBooking.component.html',
  styleUrls: ['./glsBooking.component.scss'],
})
export class GLSBookingv2 implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  shipment = <any>{ senderAddress: {}, recipientAddress: {}, collis: [], };

  loadingParcelShops: boolean = false;
  loading: boolean = false;

  glsPrivatePermission = OBPermisssionsLocal.BookGLSPrivate;
  glsAbroadPermission = OBPermisssionsLocal.BookGLSAbroad;

  externalCarrierServices: any;
  contactPersons: any;
  weightLimit: number | null = null;

  packageInfoForm = new FormGroup({
    waybillNo: new FormControl(null),
    weight: new FormControl(null, Validators.required),
    bookingType: new FormControl('', Validators.required),
  });

  addressForm = new FormGroup({
    name: new FormControl(this.shipment.recipientAddress.name, Validators.required),
    street: new FormControl(this.shipment.recipientAddress.street, Validators.required),
    contactPerson: new FormControl(),
    contactPhone: new FormControl(),
    contactEmail: new FormControl(null, Validators.email),
    zipCode: new FormControl(this.shipment.recipientAddress.zipCode, Validators.required),
    city: new FormControl(this.shipment.recipientAddress.city, Validators.required),
    countryCode: new FormControl(this.shipment.recipientAddress.countryCode, Validators.required),
    note: new FormControl(this.shipment.note, Validators.maxLength(40)),
    parcelShopId: new FormControl(this.shipment.parcelShopId),
    externalCarrierServices: new FormControl(),
    saveAddress: new FormControl(false),
  });

  constructor(
    public stepperService: StepperService,
    private shipmentService: ShipmentService,
    private messageService: MessageService,
    private authService: AuthService,
    private router: Router,
    private servicesService: ServicesService,
    private translateService: TranslateService,
    private customerService: CustomerService,
  ) {
    this.setGLSValues();
  }

  ngOnInit(): void {
    this.watchBookingType();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goBack(): void {
    this.stepperService.bookingChoice = null;
    this.router.navigate(['/']);
    this.stepperService.newShipment.collis = [];
  }

  setGLSValues(): void {
    this.stepperService.newShipment.services = null;

    this.shipment = this.stepperService.newShipment;
    this.shipment.bookingType = null;
    this.shipment.carrier = 'GLS';
    this.shipment.collis = [];
    this.shipment.collis.push({ type: 'GLS', quantity: 1, description: 'GLS', });

    const date = new Date();
    date.setDate(date.getDate() + 1);

    this.shipment.deliveryDate = formatDate(date, 'yyyy-MM-dd', 'en-US');
  }

  checkPermission(permission: any): boolean {
    return this.authService.hasPermission(permission);
  }

  private watchBookingType(): void {
    this.packageInfoForm.controls.bookingType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((bookingType) => {
        if (!bookingType) {
          return;
        }

        if (bookingType === this.shipment.bookingType) {
          return;
        }

        this.onBookingTypeChanged(bookingType as GLSBookingType);
      });
  }

  private onBookingTypeChanged(bookingType: GLSBookingType): void {
    this.resetAddressForBookingType();

    this.shipment.bookingType = bookingType;

    this.configureAddressFormByBookingType(bookingType);
    this.setServicesAndWeightValidatorByBookingType(bookingType);
  }

  private resetAddressForBookingType(): void {
    this.addressForm.reset({
      saveAddress: false,
    });

    this.shipment = this.stepperService.newShipment;
    this.shipment.recipientAddress = {};
    this.shipment.externalCarrierServices = null;
    this.shipment.parcelShop = null;
    this.shipment.parcelShopId = null;
    this.shipment.service = null;
    this.shipment.services = null;
  }

  private configureAddressFormByBookingType(bookingType: GLSBookingType): void {
    const isParcelShop = bookingType === 'PARCELSHOP';

    this.setRequiredValidator(this.addressForm.controls.parcelShopId, isParcelShop);
    this.setRequiredValidator(this.addressForm.controls.contactPerson, isParcelShop);
    this.setRequiredValidator(this.addressForm.controls.contactPhone, isParcelShop);
    this.setRequiredValidator(this.addressForm.controls.contactEmail, isParcelShop);

    if (isParcelShop) {
      this.addressForm.controls.city.disable({ emitEvent: false });
      this.addressForm.controls.zipCode.disable({ emitEvent: false });
      this.addressForm.controls.countryCode.disable({ emitEvent: false });
    } else {
      this.addressForm.controls.city.enable({ emitEvent: false });
      this.addressForm.controls.zipCode.enable({ emitEvent: false });
      this.addressForm.controls.countryCode.enable({ emitEvent: false });
    }
  }

  private setRequiredValidator(control: FormControl, required: boolean): void {
    if (required) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  formErrors(): string {
    const invalidFields: string[] = [];

    if (this.packageInfoForm.invalid) {
      for (const key in this.packageInfoForm.controls) {
        if (this.packageInfoForm.controls[key].invalid) {
          switch (key) {
            case 'weight':
              invalidFields.push(this.translateService.instant('WEIGHT'));
              break;
            case 'bookingType':
              invalidFields.push(this.translateService.instant('TYPE'));
              break;
          }
        }
      }
    }

    if (this.addressForm.invalid) {
      for (const key in this.addressForm.controls) {
        if (this.addressForm.controls[key].invalid) {
          switch (key) {
            case 'contactPerson':
              invalidFields.push(this.translateService.instant('RECEIVERNAME'));
              break;

            case 'contactPhone':
              invalidFields.push(this.translateService.instant('RECEIVERPHONE'));
              break;

            case 'contactEmail':
              invalidFields.push(this.translateService.instant('RECEIVEREMAIL'));
              break;

            case 'street':
              if (this.packageInfoForm.controls.bookingType.value !== 'PARCELSHOP') {
                invalidFields.push(this.translateService.instant('ADDRESS'));
              } else {
                invalidFields.push(
                  this.translateService.instant('PARCELSHOPNAME') +
                  this.translateService.instant('ADDRESS')
                );
              }
              break;

            case 'parcelShopId':
              invalidFields.push(this.translateService.instant('PARCELSHOPNAME'));
              break;

            case 'countryCode':
              invalidFields.push(this.translateService.instant('COUNTRYCODE'));
              break;

            case 'note':
              invalidFields.push(this.translateService.instant('NOTEABOVEMAXLENGTH'));
              break;
          }
        }
      }
    }

    return invalidFields.join(', ');
  }

  checkFormValidity(): boolean {
    if (this.packageInfoForm.valid && this.addressForm.valid) {
      return true;
    }

    this.packageInfoForm.markAllAsTouched();
    this.packageInfoForm.markAsDirty();

    this.addressForm.markAllAsTouched();
    this.addressForm.markAsDirty();

    Object.values(this.packageInfoForm.controls).forEach((control) => {
      if (control.invalid) {
        control.markAsDirty();
        control.markAsTouched();
      }
    });

    Object.values(this.addressForm.controls).forEach((control) => {
      if (control.invalid) {
        control.markAsDirty();
        control.markAsTouched();
      }
    });

    this.messageService.add({
      severity: 'warn',
      summary: this.translateService.instant('MESSAGES.MISSINGFIELDS'),
      detail: this.formErrors(),
    });

    return false;
  }

  bookShipment(): void {
    if (!this.checkFormValidity()) {
      return;
    }

    this.applyFormsToShipment();

    this.loading = true;
    this.checkSaveAddress();

    if (this.shipment.service) {
      this.shipment.services = [this.shipment.service.code];
      this.shipment.service = null;
    }

    this.shipmentService.createShipment(this.shipment).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('APPROVED'),
          detail: this.translateService.instant('MESSAGES.BOOKINGCREATED'),
        });

        this.packageInfoForm.reset({
          waybillNo: null,
          weight: null,
          bookingType: '',
        });

        this.addressForm.reset({
          saveAddress: false,
        });

        this.setGLSValues();
        this.loading = false;
      },
      error: (e) => {
        console.error("creation of gls booking failed with msg: ", e)
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('ERROR'),
          detail: this.translateService.instant('BOOKINGERROR'),
        });

        this.loading = false;
      },
    });
  }

  private applyFormsToShipment(): void {
    const packageInfo = this.packageInfoForm.getRawValue();
    const address = this.addressForm.getRawValue();

    this.shipment.bookingType = packageInfo.bookingType;
    this.shipment.reference1 = packageInfo.waybillNo;

    if (!this.shipment.collis?.length) {
      this.shipment.collis = [
        {
          type: 'GLS',
          quantity: 1,
          description: 'GLS',
        },
      ];
    }

    this.shipment.collis[0].weight = packageInfo.weight;

    this.shipment.recipientAddress = {
      ...this.shipment.recipientAddress,
      name: address.name,
      street: address.street,
      contactPerson: address.contactPerson,
      contactPhone: address.contactPhone,
      contactEmail: address.contactEmail,
      zipCode: address.zipCode,
      city: address.city,
      countryCode: address.countryCode,
      saveAddress: address.saveAddress,
    };

    this.shipment.note = address.note;
    this.shipment.parcelShopId = address.parcelShopId;
    this.shipment.externalCarrierServices = address.externalCarrierServices;
  }

  private setServicesAndWeightValidatorByBookingType(bookingType: GLSBookingType): void {
    this.servicesService
      .getExternalServicesByCarrier(this.shipment.carrier, bookingType)
      .subscribe((res: any) => {
        this.externalCarrierServices = res.services;
      });

    const weightLimits: Record<GLSBookingType, number> = {
      PARCELSHOP: 20,
      BUSINESS: 30,
      PRIVATE: 30,
      ABROAD: 50,
    };

    this.updateWeightValidator(weightLimits[bookingType]);
  }

  private updateWeightValidator(maxValue: number): void {
    this.weightLimit = maxValue;

    this.packageInfoForm.controls.weight.setValidators([
      Validators.required,
      Validators.max(maxValue),
      Validators.min(1),
    ]);

    this.packageInfoForm.controls.weight.updateValueAndValidity({
      emitEvent: false,
    });
  }

  checkSaveAddress(): void {
    if (!this.shipment?.recipientAddress?.saveAddress) {
      return;
    }

    const recipientAddress = this.shipment.recipientAddress;

    if (this.stepperService.recipientCustomer && this.stepperService.recipientCustomer.id) {
      const addressToSave = {

        customerAddreses: {
          city: recipientAddress.city,
          countryCode: recipientAddress.countryCode,
          street: recipientAddress.street,
          zipCode: recipientAddress.zipCode,
        } as CustomerAddress,
        customerId: this.stepperService.recipientCustomer.id,
      } as AddCustomerAddress;

      this.customerService.addAddress(addressToSave).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('APPROVED'),
            detail: this.translateService.instant('MESSAGES.RECEIVERADRESSSAVED'),
          });

          this.stepperService.recipientCustomer = null;
        },
        error: () => {
          this.stepperService.recipientCustomer = null;
        },
      });

      return;
    }

    const newCustomer = {
      customerAddreses: {
        city: recipientAddress.city,
        countryCode: recipientAddress.countryCode,
        street: recipientAddress.street,
        latitude: recipientAddress.latitude,
        longitude: recipientAddress.longitude,
        zipCode: recipientAddress.zipCode,
        ValidationSource: recipientAddress.validatedBy ?? null,
      },
      name: recipientAddress.name,
      customerNo: '',
      registrationNumber: recipientAddress.name,
      contactPhone: recipientAddress.contactPhone,
      contactEmail: recipientAddress.contactEmail,
      contactName: recipientAddress.contactPerson,
    } as CreateCustomer;
    this.customerService.create(newCustomer).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('APPROVED'),
          detail: this.translateService.instant('MESSAGES.RECEIVERADRESSSAVED'),
        });
      },
      error: () => {
        this.stepperService.recipientCustomer = null;
      },
    });
  }
}